import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import Button from '../common/Button';
import ReactECharts from 'echarts-for-react';

const WealthCalculator = () => {
  const { t } = useLanguage();
  const [inputs, setInputs] = useState({ income: 10000, years: 5, investRate: 20 });
  const [showResult, setShowResult] = useState(false);

  const calculateProjection = () => {
    const monthlyInvest = inputs.income * (inputs.investRate / 100);
    const r = 0.08 / 12; // 8% annual return
    
    // Calculate for 5, 10, 20 years
    const periods = [5, 10, 20];
    const data = periods.map(year => {
      const n = year * 12;
      const fv = monthlyInvest * ((Math.pow(1 + r, n) - 1) / r);
      return Math.round(fv);
    });

    return { periods, data };
  };

  const { periods, data } = calculateProjection();

  const chartOption = {
    title: { text: t('calculator.chartTitle'), left: 'center' },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: periods.map(p => `${p} Years`) },
    yAxis: { type: 'value' },
    series: [{
      data: data,
      type: 'bar',
      itemStyle: { color: '#E91E8C' },
      label: { show: true, position: 'top', formatter: '${c}' }
    }]
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6 text-gray-900">{t('calculator.title')}</h2>
            <div className="space-y-6 bg-gray-50 p-8 rounded-2xl">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('calculator.income')}</label>
                <input 
                  type="number" 
                  value={inputs.income}
                  onChange={e => setInputs({...inputs, income: Number(e.target.value)})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('calculator.invest')}</label>
                <input 
                  type="range" 
                  min="0" max="50"
                  value={inputs.investRate}
                  onChange={e => setInputs({...inputs, investRate: Number(e.target.value)})}
                  className="w-full accent-pink-600"
                />
                <div className="text-right font-bold text-pink-600">{inputs.investRate}%</div>
              </div>
              <Button onClick={() => setShowResult(true)} className="w-full">
                {t('calculator.calculate')}
              </Button>
            </div>
          </div>
          
          <div className="h-[400px] flex items-center justify-center">
            {showResult ? (
              <div className="w-full h-full">
                <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
                <p className="text-xs text-center text-gray-500 mt-4">{t('calculator.disclaimer')}</p>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <div className="text-6xl mb-4">📊</div>
                <p>Enter your data to see the magic of compound interest.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WealthCalculator;